import {
  Box,
  type IconName,
  Inline,
  Link,
  List,
  Modal,
  type ModalProps,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import {
  type XeroConnectionRequiredAction,
  XeroConnectionRequiredActionType,
} from '@omnipresentgroup/types';
import { useMemo } from 'react';
import { type NavigateFunction, useNavigate } from 'react-router';

import { XERO_CONFIGURATION_PATH } from '../../../constants';

const TEXTS = {
  title: (isPlural: boolean) => `Action${isPlural ? 's' : ''} required`,
  description: (isPlural: boolean) =>
    `To complete the Xero integration, you need to take ${
      isPlural ? 'some actions' : 'an action'
    }:`,
  actions: {
    accountMapping: 'Complete your account code mapping',
    selectOrg: 'Select your organisation',
    activateContact: 'Activate your contact',
    activateContactAction: 'Activate contact',
  },
} as const;

type ActionItem = {
  id: string;
  description: string;
  action?: () => void;
  icon?: IconName;
};

const actionHandlers: Record<
  XeroConnectionRequiredActionType,
  (
    action: XeroConnectionRequiredAction,
    navigate: NavigateFunction,
  ) => ActionItem | null
> = {
  [XeroConnectionRequiredActionType.ACCOUNT_CODE_MAPPING_INCOMPLETE]: (
    _,
    navigate,
  ) => ({
    id: XeroConnectionRequiredActionType.ACCOUNT_CODE_MAPPING_INCOMPLETE,
    description: TEXTS.actions.accountMapping,
    action: () => navigate(XERO_CONFIGURATION_PATH),
    icon: 'Settings',
  }),
  [XeroConnectionRequiredActionType.XERO_ORGANISATION_NOT_SELECTED]: (
    _,
    navigate,
  ) => ({
    id: XeroConnectionRequiredActionType.XERO_ORGANISATION_NOT_SELECTED,
    description: TEXTS.actions.selectOrg,
    action: () => navigate(XERO_CONFIGURATION_PATH),
    icon: 'Settings',
  }),
  [XeroConnectionRequiredActionType.CONTACT_NOT_ACTIVE]: (action) => ({
    id: XeroConnectionRequiredActionType.CONTACT_NOT_ACTIVE,
    description: TEXTS.actions.activateContact,
    action: action.url ? () => window.open(action.url, '_blank') : undefined,
    icon: 'ExternalLink',
  }),
};

const getActionRequired = (
  action: XeroConnectionRequiredAction,
  navigate: NavigateFunction,
): ActionItem | null => {
  const handler = actionHandlers[action.type];
  return handler ? handler(action, navigate) : null;
};

type XeroActionsRequiredModalProps = {
  modalProps: Omit<ModalProps, 'children'>;
  actions?: XeroConnectionRequiredAction[];
  handleClose: () => void;
};

export const XeroActionsRequiredModal = ({
  actions = [],
  modalProps,
  handleClose,
}: XeroActionsRequiredModalProps) => {
  const navigate = useNavigate();

  const requiredActions = useMemo(
    () =>
      actions
        .map((action) => getActionRequired(action, navigate))
        .filter(Boolean),
    [actions, navigate],
  );

  const multipleActions = requiredActions.length > 1;

  return (
    <Modal {...modalProps} span={6}>
      <Modal.Header
        title={TEXTS.title(multipleActions)}
        onClose={handleClose}
      />
      <Modal.Body>
        <Stack gap="12">
          <Typography as="span" size="16">
            {TEXTS.description(multipleActions)}
          </Typography>

          <List
            showDisc={false}
            gap="4"
            items={requiredActions}
            keyExtractor="id"
            render={(item) =>
              item?.action ? (
                <Link
                  size="14"
                  onClick={() => item.action?.()}
                  icon={item.icon}
                >
                  {item.description}
                </Link>
              ) : (
                <Inline gap="4" align="center">
                  <Box
                    css={{
                      '&::before': {
                        content: '• ',
                      },
                    }}
                  />
                  <Typography as="span" size="14">
                    {item?.description}
                  </Typography>
                </Inline>
              )
            }
          />
        </Stack>
      </Modal.Body>
    </Modal>
  );
};
